import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    FiCheck,
    FiUploadCloud,
    FiLoader,
    FiLock,
    FiPlus,
    FiRefreshCw,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { FreePlanLimitModal, useTransactionUsage } from '../../components/FreePlan';
import { Header } from '../../components/layout/Header';
import { Sidebar } from '../../components/layout/Sidebar';
import { useAuth, useFinance } from '../../contexts';
import { usePreferences } from '../../contexts/PreferencesContext';
import { CategoriaSubCategoriaModal, QuickAddModal, SearchableSelect, SearchableSelectOption, toast } from '../../components/ui';
import { api, ApiNetworkError } from '../../services/api';
import { opcoesAgrupadas } from '../../utils/categoriasAgrupadas';
import styles from './LancarReceita.module.css';

interface Categoria {
    id: string;
    nome: string;
    tipo: string;
}

interface SubCategoria {
    id: string;
    codigo: string;
    nome: string;
    categoria: { id: string; nome: string; tipo: string };
}

interface FormaPagamento {
    id: string;
    nome: string;
    tipo: string;
}

type ModalAberto = 'categoria' | 'subcategoria' | 'formaPagamento' | null;

export const LancarReceita = () => {
    const navigate = useNavigate();
    const { refreshData } = useFinance();
    const { t, currencySymbol, prefs } = usePreferences();
    const { hasPermission, hasFeature } = useAuth();
    const canCreate = hasPermission('LANCAMENTOS', 'CREATE');
    // O botao de importar depende da feature do plano E da permissao do perfil.
    const podeImportar = hasFeature('import') && hasPermission('IMPORTACOES', 'CREATE');
    // MAN-364: no plano Gratuito, com o limite atingido a tela fica bloqueada.
    const usoGratuito = useTransactionUsage();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [categorias, setCategorias] = useState<Categoria[]>([]);
    const [subCategorias, setSubCategorias] = useState<SubCategoria[]>([]);
    const [formasPagamento, setFormasPagamento] = useState<FormaPagamento[]>([]);

    const [data, setData] = useState(new Date().toISOString().split('T')[0]);
    const [categoriaId, setCategoriaId] = useState('');
    const [subCategoriaId, setSubCategoriaId] = useState('');
    const [formaPagamentoId, setFormaPagamentoId] = useState('');
    const [valor, setValor] = useState('');
    const [informacoesAdicionais, setInformacoesAdicionais] = useState('');
    
    // Categorias dentro do grupo, na ordem do plano de contas (MAN-130).
    const subCategoriaOptions = useMemo<SearchableSelectOption[]>(
        () => opcoesAgrupadas(subCategorias, categorias, t),
        [subCategorias, categorias, t],
    );

    const categoriaOptions = useMemo<SearchableSelectOption[]>(
        () => categorias.map((c) => ({ value: c.id, label: t(c.nome) })),
        [categorias, t],
    );

    const formaPagamentoOptions = useMemo<SearchableSelectOption[]>(
        () =>
            formasPagamento.map((fp) => ({
                value: fp.id,
                label: t(fp.nome),
            })),
        [formasPagamento, t],
    );

    const [modalAberto, setModalAberto] = useState<ModalAberto>(null);

    const selectedSubCategoria = subCategorias.find(s => s.id === subCategoriaId) ?? null;


    const formatCurrency = (value: string) => {
        const numericValue = value.replace(/\D/g, '');
        if (!numericValue) return '';
        const numValue = parseFloat(numericValue) / 100;
        return numValue.toLocaleString(prefs.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const parseValorToNumber = (formattedValue: string): number => {
        if (!formattedValue) return 0;
        if (prefs.language === 'en-US') {
            return parseFloat(formattedValue.replace(/,/g, '')) || 0;
        }
        return parseFloat(formattedValue.replace(/\./g, '').replace(',', '.')) || 0;
    };

    const loadOptions = useCallback(async () => {
        try {
            setLoading(true);
            const [categoriasData, subsData, formasData] = await Promise.all([
                api.getCategorias('ENTRADA'),
                api.getSubCategorias({ tipo: 'ENTRADA' }),
                api.getFormasPagamentoNorm({ tipo: 'ENTRADA' }),
            ]);
            setCategorias(categoriasData);
            setSubCategorias(subsData);
            setFormasPagamento(formasData);
        } catch (error) {
            console.error('Erro ao carregar opções:', error);
            const msg = error instanceof ApiNetworkError ? error.friendlyMessage : t('common.erroCarregar');
            toast(msg, 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadOptions(); }, [loadOptions]);

    const resetForm = () => {
        setData(new Date().toISOString().split('T')[0]);
        setCategoriaId('');
        setSubCategoriaId('');
        setFormaPagamentoId('');
        setValor('');
        setInformacoesAdicionais('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!subCategoriaId || !formaPagamentoId || !valor) {
            toast(t('common.erroCampos'), 'error');
            return;
        }
        setSaving(true);
        try {
            const subCategoria = subCategorias.find(s => s.id === subCategoriaId);
            const formaPagamento = formasPagamento.find(f => f.id === formaPagamentoId);
            await api.createLancamento({
                categoria: 'RECEITA',
                data,
                subCategoria: subCategoria?.nome || '',
                formaPagamento: formaPagamento?.nome || '',
                valor: parseValorToNumber(valor),
                informacoesAdicionais: informacoesAdicionais || undefined,
            });
            toast(t('receita.sucesso'), 'success');
            resetForm();
            refreshData();
        } catch (error) {
            console.error('Erro ao salvar lançamento:', error);
            const msg = error instanceof ApiNetworkError
                ? error.friendlyMessage
                : error instanceof Error ? error.message : 'Erro ao salvar lançamento.';
            toast(msg, 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleCriarCategoria = async (nome: string) => {
        const nova = await api.createCategoria({ tipo: 'ENTRADA', nome, ordem: 0 });
        const option = { value: nova.id, label: nova.nome };
        setCategorias((prev) => [...prev, nova]);
        return option;
    };

    const handleCriarSubCategoria = async (nome: string, categoriaId: string) => {
        return api.createSubCategoria({ categoriaId, nome });
    };

    const handleBuscarSubCategorias = useCallback(async (categoriaId: string) => {
        return api.getSubCategorias({ categoriaId });
    }, []);

    const handleConfirmarCategoriaSubCategoria = ({ subCategoriaId: novaId, subCategoriaNome, categoriaId: catId, categoriaNome }: {
        subCategoriaId: string;
        subCategoriaNome: string;
        categoriaId: string;
        categoriaNome: string;
    }) => {
    setSubCategorias((prev) => {
        const jaExiste = prev.some((s) => s.id === novaId);
        if (jaExiste) return prev;
        return [...prev, { id: novaId, codigo: '', nome: subCategoriaNome, categoria: { id: catId, nome: categoriaNome, tipo: 'ENTRADA' } }];
    });
    setCategoriaId(catId);
    setSubCategoriaId(novaId);
};

    const handleAddFormaPagamento = async ({ nome }: { nome: string }) => {
        const nova = await api.createFormaPagamentoNorm({ nome });
        setFormasPagamento((prev) => [...prev, nova]);
        setFormaPagamentoId(nova.id);
    };

    const categoriaDaSubcategoriaSelecionada = subCategoriaId
        ? subCategorias.find(s => s.id === subCategoriaId)?.categoria?.id
        : undefined;

    if (usoGratuito?.reached && usoGratuito.limit) {
        return (
            <div className={styles.layout}>
                <Sidebar />
                <main className={styles.main}>
                    <Header title={t('receita.titulo')} subtitle={t('receita.subtitulo')} />
                    <div className={styles.content} />
                    <FreePlanLimitModal limite={usoGratuito.limit} onClose={() => navigate('/home')} />
                </main>
            </div>
        );
    }

    if (loading) {
        return (
            <div className={styles.layout}>
                <Sidebar />
                <main className={styles.main}>
                    <Header title={t('receita.titulo')} subtitle={t('receita.subtitulo')} />
                    <div className={styles.content}>
                        <div className={styles.loading}>
                            <FiLoader size={32} />
                            <span>Carregando...</span>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className={styles.layout}>
            <Sidebar />
            <main className={styles.main}>
                <Header title={t('receita.titulo')} subtitle={t('receita.subtitulo')} />
                <div className={styles.content}>
                    <div className={styles.pageHeader}>
                        {podeImportar && (
                            <button className={styles.importBtn} onClick={() => navigate('/importacoes')}>
                                <FiUploadCloud size={16} />
                                Importar planilha
                            </button>
                        )}
                    </div>

                    <div className={styles.formContainer}>
                        <form className={styles.formCard} onSubmit={handleSubmit}>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label>{t('receita.dataRecebimento')} <span>*</span></label>
                                    <input
                                        type="date"
                                        value={data}
                                        onChange={(e) => setData(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('receita.tipo')} <span>*</span></label>
                                    <div className={styles.selectWithAction}>
                                        <SearchableSelect
                                            options={subCategoriaOptions}
                                            value={subCategoriaId}
                                            onChange={setSubCategoriaId}
                                            placeholder={t('common.selecione')}
                                            searchPlaceholder={t('lanc.buscarCategoriaOuGrupo')}
                                            required
                                            fullWidth
                                        />
                                        {(
                                            <button
                                                type="button"
                                                className={styles.addBtn}
                                                onClick={() => setModalAberto('subcategoria')}
                                                title={t('modal.subcategoria.titulo')}
                                            >
                                                <FiPlus size={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {selectedSubCategoria && (
                                    <div className={styles.formGroup}>
                                        <label>{t('lanc.grupo')}</label>
                                        <div className={styles.lockedField}>
                                            <span>{t(selectedSubCategoria.categoria.nome)}</span>
                                        </div>
                                    </div>
                                )}

                                <div className={styles.formGroup}>
                                    <label>{t('receita.formaPagamento')} <span>*</span></label>
                                    <div className={styles.selectWithAction}>
                                        <SearchableSelect
                                            options={formaPagamentoOptions}
                                            value={formaPagamentoId}
                                            onChange={setFormaPagamentoId}
                                            placeholder={t('common.selecione')}
                                            required
                                            fullWidth
                                        />
                                        {(
                                            <button
                                                type="button"
                                                className={styles.addBtn}
                                                onClick={() => setModalAberto('formaPagamento')}
                                                title="Adicionar forma de pagamento"
                                            >
                                                <FiPlus size={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('receita.valor')} <span>*</span></label>
                                    <div className={styles.inputWithPrefix}>
                                        <span className={styles.currencyPrefix}>{currencySymbol}</span>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={valor}
                                            onChange={(e) => setValor(formatCurrency(e.target.value))}
                                            placeholder={prefs.language === 'en-US' ? '0.00' : '0,00'}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                    <label>{t('receita.infoAdicionais')}</label>
                                    <textarea
                                        value={informacoesAdicionais}
                                        onChange={(e) => setInformacoesAdicionais(e.target.value)}
                                        placeholder={t('receita.placeholder')}
                                        maxLength={500}
                                    />
                                </div>
                            </div>

                            <div className={styles.formActions}>
                                <button type="button" className={`${styles.btn} ${styles.btnLimpar}`} onClick={resetForm}>
                                    <FiRefreshCw size={18} /> {t('common.limpar')}
                                </button>
                                <button type="submit" className={`${styles.btn} ${styles.btnLancar}`} disabled={saving || !canCreate} title={!canCreate ? 'Você não tem permissão para lançar receitas' : undefined}>
                                    {saving ? <><FiLoader size={18} /> {t('common.salvando')}</> : <><FiCheck size={18} /> {t('receita.btn')}</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </main>

            {modalAberto === 'subcategoria' && (
                <CategoriaSubCategoriaModal
                    categorias={categorias.map((c) => ({ value: c.id, label: t(c.nome) }))}
                    tipo="ENTRADA"
                    defaultCategoriaId={categoriaDaSubcategoriaSelecionada}
                    t={t}                    
                    onConfirm={handleConfirmarCategoriaSubCategoria}
                    onCriarCategoria={handleCriarCategoria}
                    onCriarSubCategoria={handleCriarSubCategoria}
                    onBuscarSubCategorias={handleBuscarSubCategorias}
                    onClose={() => setModalAberto(null)}
                />
            )}

            {modalAberto === 'formaPagamento' && (
                <QuickAddModal
                    title="Nova Forma de Pagamento"
                    fields={{
                        nome: { label: 'Nome', placeholder: 'Ex: Pix' },
                    }}
                    onConfirm={handleAddFormaPagamento}
                    onClose={() => setModalAberto(null)}
                />
            )}
        </div>
    );
};